/**
 * Capture / match / restore helpers for same-page focus + caret.
 * Turbo lifecycle install lives in `focus-restore-policy.ts`.
 * Selection helpers: `focus-restore-selection.ts`.
 *
 * Rich-text (TinyMCE): capture uses the editable surface identity; restore is deferred until the
 * editor finishes remount/boot via `tryRestoreDeferredRichTextFocus` (controller hook).
 *
 * Always uses `preventScroll` so layout `turbo-refresh-scroll=preserve` stays authoritative.
 */

import {
   captureSelection,
   isContentEditableSurface,
   isTextLikeInput,
   restoreSelection,
   type FocusTextSelection,
} from "./focus-restore-selection";

/** Stable handle for the form that scoped a name-based field identity. */
export type FormRef =
   | { kind: "id"; id: string }
   | { kind: "index"; index: number };

type FocusFieldIdentity =
   | { kind: "id"; id: string }
   | { kind: "name"; name: string; form: FormRef | null };

export type FocusSnapshot = {
   identity: FocusFieldIdentity;
   selection: FocusTextSelection | null;
   /**
    * The control's own value at capture time, for text-like inputs/textareas only (null otherwise,
    * including rich-text/contenteditable surfaces). Replace-path restore writes this back over a
    * server-rendered value that differs, so a slower render never undoes what the person typed
    * after the snapshot was taken.
    */
   liveValue: string | null;
};

/** Suite deliberate-focus signal used by rich-text. */
const RICH_TEXT_AUTOFOCUS_SELECTOR = '[data-rich-text-editor-autofocus-value="true"]';

/** Editable surface of the shared rich-text editor (TinyMCE target). */
const RICH_TEXT_EDITABLE_SELECTOR = '[data-rich-text-editor-target="editable"]';

const DELIBERATE_AUTOFOCUS_SELECTOR = `[autofocus], ${RICH_TEXT_AUTOFOCUS_SELECTOR}`;

/** Held when the matched control is a rich-text surface that must wait for editor boot. */
let deferredRichTextSnapshot: FocusSnapshot | null = null;

/** True when focus is null, body, or documentElement (nothing meaningful to protect). */
export function isDocumentRootFocus(el: Element | null): boolean {
   return el == null || el === document.body || el === document.documentElement;
}

function associatedForm(el: Element): HTMLFormElement | null {
   if (
      el instanceof HTMLInputElement ||
      el instanceof HTMLTextAreaElement ||
      el instanceof HTMLSelectElement ||
      el instanceof HTMLButtonElement
   ) {
      return el.form;
   }
   if (el instanceof HTMLElement) {
      return el.closest("form");
   }
   return null;
}

export function formRef(form: HTMLFormElement | null): FormRef | null {
   if (!form) {
      return null;
   }
   if (form.id) {
      return { kind: "id", id: form.id };
   }
   const index = Array.prototype.indexOf.call(document.forms, form);
   return index >= 0 ? { kind: "index", index } : null;
}

function captureIdentity(el: Element): FocusFieldIdentity | null {
   if (el.id) {
      return { kind: "id", id: el.id };
   }

   const name = el.getAttribute("name");
   if (!name) {
      return null;
   }

   return { kind: "name", name, form: formRef(associatedForm(el)) };
}

function isRichTextEditable(el: Element): el is HTMLElement {
   return el instanceof HTMLElement && el.matches(RICH_TEXT_EDITABLE_SELECTOR);
}

/**
 * When focus is inside a nested node of a contenteditable / rich-text surface, capture against
 * the surface that carries stable identity (id/name), not the ephemeral child.
 */
function resolveCaptureSurface(el: HTMLElement): HTMLElement {
   const richTextEditable = el.closest(RICH_TEXT_EDITABLE_SELECTOR);
   if (richTextEditable instanceof HTMLElement) {
      return richTextEditable;
   }

   if (isContentEditableSurface(el)) {
      return el;
   }

   const contentEditableAncestor = el.closest('[contenteditable="true"], [contenteditable=""]');
   if (contentEditableAncestor instanceof HTMLElement) {
      return contentEditableAncestor;
   }

   return el;
}

export function captureFocusSnapshot(active: Element | null = document.activeElement): FocusSnapshot | null {
   if (!(active instanceof HTMLElement) || isDocumentRootFocus(active)) {
      return null;
   }

   const surface = resolveCaptureSurface(active);
   const identity = captureIdentity(surface);
   if (!identity) {
      return null;
   }

   return {
      identity,
      selection: captureSelection(surface),
      liveValue: isTextLikeInput(surface) ? surface.value : null,
   };
}

function resolveNamedControl(form: HTMLFormElement, name: string): HTMLElement | null {
   const found = form.elements.namedItem(name);
   if (found instanceof HTMLElement) {
      return found;
   }
   if (typeof RadioNodeList !== "undefined" && found instanceof RadioNodeList) {
      const first = found[0];
      return first instanceof HTMLElement ? first : null;
   }
   // happy-dom / older engines may return a NodeList-like without RadioNodeList
   if (found && typeof (found as NodeList).length === "number") {
      const first = (found as NodeList).item(0);
      return first instanceof HTMLElement ? first : null;
   }
   return null;
}

/**
 * Prefer `id`, else `name` within the same form. Never fall back to a document-wide `[name=…]`
 * (that can match another form). No match → null.
 */
function findFocusTarget(identity: FocusFieldIdentity): HTMLElement | null {
   if (identity.kind === "id") {
      const byId = document.getElementById(identity.id);
      return byId instanceof HTMLElement ? byId : null;
   }

   const form = resolveForm(identity.form);
   if (!form) {
      // Name-only outside a form (or form gone / unresolvable) → restore nothing.
      return null;
   }
   return resolveNamedControl(form, identity.name);
}

export function resolveForm(form: FormRef | null): HTMLFormElement | null {
   if (!form) {
      return null;
   }
   if (form.kind === "id") {
      const el = document.getElementById(form.id);
      return el instanceof HTMLFormElement ? el : null;
   }
   return document.forms.item(form.index) ?? null;
}

export function isFocusable(el: HTMLElement): boolean {
   if (el instanceof HTMLInputElement && el.type === "hidden") {
      return false;
   }
   if (
      "disabled" in el &&
      Boolean((el as HTMLInputElement | HTMLButtonElement | HTMLSelectElement | HTMLTextAreaElement).disabled)
   ) {
      return false;
   }
   if (el.hasAttribute("disabled")) {
      return false;
   }
   if (el.hidden || el.getAttribute("aria-hidden") === "true") {
      return false;
   }

   const style = window.getComputedStyle(el);
   if (style.display === "none" || style.visibility === "hidden") {
      return false;
   }

   return true;
}

function hasDeliberateAutofocusSignal(root: ParentNode = document): boolean {
   return root.querySelector(DELIBERATE_AUTOFOCUS_SELECTOR) != null;
}

/**
 * Resolves the focusable element that a deliberate autofocus signal points at.
 * Native `[autofocus]` → that element. Suite rich-text autofocus on the host → its editable
 * surface when already live; otherwise null (controller focuses on boot).
 */
function findDeliberateAutofocusTarget(root: ParentNode = document): HTMLElement | null {
   const candidates = root.querySelectorAll(DELIBERATE_AUTOFOCUS_SELECTOR);
   for (const el of candidates) {
      if (!(el instanceof HTMLElement)) {
         continue;
      }

      if (el.matches(RICH_TEXT_AUTOFOCUS_SELECTOR) || el.closest(RICH_TEXT_AUTOFOCUS_SELECTOR)) {
         const host = el.matches(RICH_TEXT_AUTOFOCUS_SELECTOR)
            ? el
            : (el.closest(RICH_TEXT_AUTOFOCUS_SELECTOR) as HTMLElement);
         const editable =
            host.matches(RICH_TEXT_EDITABLE_SELECTOR)
               ? host
               : host.querySelector(RICH_TEXT_EDITABLE_SELECTOR);
         if (
            editable instanceof HTMLElement &&
            isContentEditableSurface(editable) &&
            isFocusable(editable)
         ) {
            return editable;
         }
         // Editor not live yet — rich-text controller will focus on boot; still a deliberate win.
         continue;
      }

      if (isFocusable(el)) {
         return el;
      }
   }
   return null;
}

/**
 * Ensures the deliberate autofocus target receives focus when present and focusable.
 * Returns true when a deliberate autofocus signal exists (prior-field restore must not run),
 * whether or not we could apply focus yet (e.g. rich-text still booting).
 */
export function applyDeliberateAutofocus(root: ParentNode = document): boolean {
   if (!hasDeliberateAutofocusSignal(root)) {
      return false;
   }

   const target = findDeliberateAutofocusTarget(root);
   if (target && document.activeElement !== target) {
      target.focus({ preventScroll: true });
   }
   return true;
}

function elementMatchesIdentity(el: Element, identity: FocusFieldIdentity): boolean {
   if (identity.kind === "id") {
      return el.id === identity.id;
   }

   if (el.getAttribute("name") !== identity.name) {
      return false;
   }

   if (!identity.form) {
      // Snapshot was name-only outside a form; treat any same-name control as a match.
      return true;
   }

   const form = associatedForm(el);
   if (!form) {
      return false;
   }
   if (identity.form.kind === "id") {
      return form.id === identity.form.id;
   }
   return Array.prototype.indexOf.call(document.forms, form) === identity.form.index;
}

/**
 * True when focus is on an interactive control the user can mean to stay on
 * (form controls, links, contenteditable / rich-text, positive tabindex).
 * Body, nothing, and non-control containers (morph residue) are not interactive.
 */
function isInteractiveControl(el: Element | null): el is HTMLElement {
   if (!(el instanceof HTMLElement) || isDocumentRootFocus(el)) {
      return false;
   }

   if (
      el instanceof HTMLInputElement ||
      el instanceof HTMLTextAreaElement ||
      el instanceof HTMLSelectElement ||
      el instanceof HTMLButtonElement
   ) {
      return true;
   }

   if (el instanceof HTMLAnchorElement && el.hasAttribute("href")) {
      return true;
   }

   if (isContentEditableSurface(el) || isRichTextEditable(el)) {
      return true;
   }

   if (el.closest(RICH_TEXT_EDITABLE_SELECTOR) || el.closest('[contenteditable="true"], [contenteditable=""]')) {
      return true;
   }

   const tabindex = el.getAttribute("tabindex");
   if (tabindex != null && tabindex !== "" && Number(tabindex) >= 0) {
      return true;
   }

   return false;
}

/**
 * Decision 10: only treat focus as “user already moved” when it is on a different interactive
 * control. Morph residue on a useless container, body, or nothing must still allow restore.
 * Nested focus inside the same rich-text surface counts as the snapshot surface.
 * A null identity (nothing nameable had focus) makes every interactive control a different one.
 */
export function userAlreadyMovedToDifferentControl(active: Element | null, identity: FocusFieldIdentity | null): boolean {
   if (!isInteractiveControl(active)) {
      return false;
   }

   if (identity === null) {
      return true;
   }

   if (elementMatchesIdentity(active, identity)) {
      return false;
   }

   // Focus inside a nested node of the snapshot rich-text surface is still “on” that surface.
   const activeRichSurface = active.closest(RICH_TEXT_EDITABLE_SELECTOR);
   if (activeRichSurface instanceof HTMLElement && elementMatchesIdentity(activeRichSurface, identity)) {
      return false;
   }

   return true;
}

type RestoreFocusOptions = {
   root?: ParentNode;
   /**
    * When true, apply restore even if the target is a rich-text editable.
    * Used after TinyMCE boot; the Turbo lifecycle path leaves rich-text deferred instead.
    */
   allowRichTextImmediate?: boolean;
};

/**
 * Applies a captured snapshot when the matching control still exists and restore is allowed.
 * Rich-text editables are not restored here on the Turbo path — use
 * `tryRestoreDeferredRichTextFocus` after the editor remounts.
 *
 * Owns the deferred-rich-text lifecycle: any non-null apply starts by dropping a prior deferral,
 * and only re-arms it when this snapshot must wait for TinyMCE boot.
 *
 * When the response marks a deliberate autofocus target, that target receives focus and
 * prior-field restore does not run.
 */
export function restoreFocusSnapshot(snapshot: FocusSnapshot | null, options?: RestoreFocusOptions): boolean {
   if (!snapshot) {
      return false;
   }

   // Applying a snapshot supersedes any earlier deferred restore; re-arm only if we defer again.
   deferredRichTextSnapshot = null;

   const root = options?.root ?? document;
   if (applyDeliberateAutofocus(root)) {
      return false;
   }

   const target = findFocusTarget(snapshot.identity);
   if (!target || !isFocusable(target)) {
      return false;
   }

   // TinyMCE destroys and recreates the editable surface after morph; focusing before boot is useless.
   // Defer to the rich-text controller (tryRestoreDeferredRichTextFocus) unless already post-boot.
   if (isRichTextEditable(target) && !options?.allowRichTextImmediate) {
      deferredRichTextSnapshot = snapshot;
      return false;
   }

   const active = document.activeElement;
   if (userAlreadyMovedToDifferentControl(active, snapshot.identity)) {
      return false;
   }

   // Same element node already focused (e.g. open native <select>, Drive morph that did not
   // replace the control): calling focus again dismisses the options list. Skip re-focus;
   // still re-apply caret/selection when we have a snapshot for text-like controls.
   if (active !== target) {
      target.focus({ preventScroll: true });
   }

   // Decision 4 (replace path): the render may have echoed back a value shorter/other than what
   // the person kept typing after the snapshot. Text-like controls win with the live value so the
   // caret below applies against the text the person actually has, not a stale server render.
   if (isTextLikeInput(target) && snapshot.liveValue != null && target.value !== snapshot.liveValue) {
      target.value = snapshot.liveValue;
   }

   restoreSelection(target, snapshot.selection);
   return true;
}

/**
 * Consumes a deferred rich-text snapshot after the editor is live again (post-remount / boot).
 * Call from the rich-text controller once TinyMCE has finished init for this editable.
 * No-ops when the deferred identity does not match this surface, the surface is not ready, or
 * restore is blocked (autofocus, user moved, gone, not focusable).
 *
 * When the surface is not ready yet, the deferred snapshot is kept for a later attempt.
 * All terminal outcomes are owned by `restoreFocusSnapshot`.
 */
export function tryRestoreDeferredRichTextFocus(editable: HTMLElement): boolean {
   const snapshot = deferredRichTextSnapshot;
   if (!snapshot) {
      return false;
   }

   const target = findFocusTarget(snapshot.identity);
   if (!target || target !== editable) {
      return false;
   }

   // Editor not live yet — keep the deferred snapshot for a later attempt.
   if (!isContentEditableSurface(editable)) {
      return false;
   }

   return restoreFocusSnapshot(snapshot, { allowRichTextImmediate: true });
}

/** Clears deferred rich-text restore state (cross-page navigation, uninstall, skip paths). */
export function clearDeferredRichTextSnapshot(): void {
   deferredRichTextSnapshot = null;
}
